<script lang="ts">
  import { onMount } from 'svelte';
  import { figureData, FIGURE_SCALE } from './figureData.js';

  interface Props {
    figures?: Array<{ title?: string; subtitle?: string }>;
  }

  let { figures: figureOverrides = [] }: Props = $props();

  // Get figures 2, 3, 4 (THREE, BRACKET, DOUBLE THREE)
  const figures = figureData.slice(2, 5).map((fig, i) => ({
    ...fig,
    title: figureOverrides[i]?.title || fig.title,
    subtitle: figureOverrides[i]?.subtitle || fig.subtitle,
  }));

  let visibleFigures = $state([]);
  let containerElement: HTMLElement;

  onMount(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(
              (entry.target as HTMLElement).dataset.index || '0'
            );
            if (!visibleFigures.includes(index)) {
              visibleFigures = [...visibleFigures, index];

              // Original animation timing for indices 2, 3, 4
              let baseDelay = 0;
              if (index === 0) baseDelay = 1250; // Originally index 2
              if (index === 1) baseDelay = 1750; // Originally index 3
              if (index === 2) baseDelay = 2250; // Originally index 4

              const svg = entry.target.querySelector('svg');
              if (svg) {
                const animateElements = svg.querySelectorAll('animate');
                animateElements.forEach((anim, animIndex) => {
                  let delay = baseDelay;

                  // Index 0: THREE (original index 2) - 4 animates (2 paths with 2 layers each)
                  // Top paths animate together, then bottom paths
                  if (index === 0) {
                    delay += animIndex < 2 ? 0 : 2500;
                  }
                  // Index 1: BRACKET (original index 3) - 4 animates (2 paths with 2 layers each)
                  // Top paths animate together, then bottom paths
                  else if (index === 1) {
                    delay += animIndex < 2 ? 0 : 2500;
                  }
                  // Index 2: DOUBLE THREE (original index 4) - 12 animates (6 paths with 2 layers each)
                  // Sequence: Path 5 (Top Left) → Path 6 (Top Arc) → Path 4 (Top Right) → Path 1 (Bottom Left) → Path 3 (Bottom Arc) → Path 2 (Bottom Right)
                  else if (index === 2) {
                    // Path 5 (Top Left) -
                    if (animIndex === 8 || animIndex === 9) delay += 0;
                    // Path 6 (Top Arc) -
                    else if (animIndex === 10 || animIndex === 11)
                      delay += 2000;
                    // Path 4 (Top Right) -
                    else if (animIndex === 6 || animIndex === 7) delay += 2000;
                    // Path 1 (Bottom Left) -
                    else if (animIndex === 0 || animIndex === 1) delay += 4000;
                    // Path 3 (Bottom Arc) -
                    else if (animIndex === 4 || animIndex === 5) delay += 3200;
                    // Path 2 (Bottom Right) -
                    else if (animIndex === 2 || animIndex === 3) delay += 6000;
                  }

                  setTimeout(() => {
                    anim.beginElement();
                  }, delay);
                });
              }
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    // Only observe cards within this component's container
    if (containerElement) {
      const cards = containerElement.querySelectorAll('.figure-card');
      cards.forEach((card) => observer.observe(card));
    }

    return () => observer.disconnect();
  });
</script>

<div bind:this={containerElement} class="figures-wrapper container">
  <div class="figures-grid">
    {#each figures as figure, index}
      <div class="figure-card" data-index={index}>
        <div class="figure-title">{figure.title}</div>
        <div class="figure-subtitle">{figure.subtitle}</div>
        <svg
          width={140 * FIGURE_SCALE}
          height={(figure.height / 2) * FIGURE_SCALE}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 280 {figure.height}"
        >
          {@html figure.svg}
        </svg>
      </div>
    {/each}
  </div>
</div>

<style>
  .figures-wrapper {
    overflow-x: hidden;
  }

  .container {
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .figures-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
    margin-top: 30px;
    justify-items: center;
  }

  @media (max-width: 768px) {
    .figures-grid {
      gap: 0;
    }

    .container {
      padding: 10px;
    }

    .figure-card {
      padding: 5px;
    }

    .figure-card svg {
      width: 100%;
      max-width: 100px;
      height: auto;
    }

    .figure-title {
      font-size: 0.875em;
      min-height: 2.5em;
    }

    .figure-subtitle {
      display: none;
    }
  }

  .figure-card {
    padding: 5px;
    border-radius: 8px;
    width: 100%;
    max-width: 400px;
    display: flex;
    flex-direction: column;
  }

  .figure-title {
    text-align: center;
    font-weight: bold;
    color: #2c3e50;
    font-size: 1.2em;
    min-height: 1.5em;
  }

  .figure-subtitle {
    text-align: center;
    margin-bottom: 15px;
    font-size: 1em;
    color: #666;
    min-height: 3em;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    display: block;
    margin: 0 auto;
    margin-top: auto;
    width: 100%;
    height: auto;
    max-width: 100%;
  }
</style>
